/**
 * The stage's own sounds (04 §5): the badge blip, the challenging stage's phrase, its tally and
 * the fanfare for a perfect one, and game over. All original tunes (rule 10). Pure.
 */

import { LEAD_IN, stepSeconds } from '../music/clock';
import { GAMEOVER } from '../music/stings';
import { midiToHz } from '../notes';
import type { VoiceSpec } from '../voice';
import { BASS, hit, LEAD, run, SECOND, type Sfx, tune } from './kit';

/**
 * A stage begins and its badge goes up: two quick pips a fourth apart, G5 to C6 - the dominant
 * and tonic of the opening jingle, which is playing when the first stage's badge appears.
 */
export const badge: Sfx = () => [{ ...run('pulse', [79, 84], 0.05, 0.12, 0.16), width: 0.25 }];

/**
 * CHALLENGING STAGE: a jaunty two seconds in F - a skipping 3 + 3 + 2 up the triad and back down
 * the sixth, then four running notes over the dominant and home.
 */
export const challenge: Sfx = () => [
  ...tune('F5 . . A5 . . C6 . | D6 . . C6 . . A5 . | Bb5 . A5 . G5 . E5 . | F5 . . .', 0.075, LEAD),
  ...tune('A4 . . C5 . . F5 . | F5 . . A5 . . F5 . | G5  . F5 . E5 . C5 . | A4 . . .', 0.075, SECOND),
  ...tune('F3 . . . C4 . . . | D3 . . . A3 . . . | C3  . . . C4 . . . | F3 . . .', 0.075, BASS),
];

/** The scale the tally counts up: C major pentatonic from C5. */
const COUNT = [72, 74, 76, 79, 81, 84, 86, 88] as const;
const BLIP = 0.06;

/** Blips in a tally: one for every five hits, at least one, eight for all forty. */
export function tallyBlips(hits: number): number {
  const n = Number.isFinite(hits) ? Math.ceil(hits / 5) : 1;
  return Math.max(1, Math.min(COUNT.length, n));
}

/** The count: square blips climbing the scale, one every 60 ms. */
function count(blips: number): VoiceSpec[] {
  return Array.from({ length: blips }, (_, i): VoiceSpec => ({
    ...hit('pulse', midiToHz(COUNT[i] ?? COUNT[0]), 0.05, 0.16),
    at: i * BLIP,
    width: 0.5,
  }));
}

/** NUMBER OF HITS: the count (`n` is the hits), then where it stopped struck again and an octave up. */
export const tally: Sfx = (_r, n) => {
  const blips = tallyBlips(n);
  const top = COUNT[blips - 1] ?? COUNT[0];
  const at = blips * BLIP + 0.04;
  return [
    ...count(blips),
    { ...hit('pulse', midiToHz(top), 0.1, 0.2), at, width: 0.25 },
    { ...hit('pulse', midiToHz(top + 12), 0.3, 0.2), at: at + 0.1, width: 0.25 },
    { ...hit('triangle', midiToHz(top - 12), 0.3, 0.2), at: at + 0.1 },
  ];
};

/** PERFECT!: the full count, then a fanfare in C - a dotted call on the tonic and a climb to the top C. */
export const perfect: Sfx = () => {
  const at = COUNT.length * BLIP + 0.06;
  return [
    ...count(COUNT.length),
    ...tune('C6 . C6 C6 E6 . G6 . . E6 G6 . C7 . . . . .', 0.085, LEAD, at),
    ...tune('G5 . G5 G5 C6 . E6 . . C6 E6 . G6 . . . . .', 0.085, SECOND, at),
    ...tune('C4 . .  .  C4 . G3 . . .  G3 . C4 . . . . .', 0.085, BASS, at),
  ];
};

/**
 * GAME OVER: a falling phrase down C minor, two octaves from G5 to C4 in two and a half seconds.
 * The game starts the `gameover` track on the same event, so this is written as a line of that
 * piece: in its key, on its sixteenths (and its lead-in), a broken chord falling under its tune.
 */
export const gameOver: Sfx = () => {
  const step = stepSeconds(GAMEOVER.bpm);
  return [
    ...tune('G5 . Eb5 . C5 . . Bb4 . G4 . . Eb4 . . C4 . . . .', step, LEAD, LEAD_IN),
    ...tune('C3 . .   . .  . . G2  . .  . . .   . . C2 . . . .', step, BASS, LEAD_IN),
  ];
};
